import type { SubmitHandler } from 'react-hook-form';
import { useErrors, useUntrustedData } from '@auth0/auth0-acul-react/login-id';
import Cta from 'creative-ui/uikit/Cta';
import CtaContainer from 'creative-ui/uikit/CtaContainer';
import Input from 'creative-ui/uikit/Input';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AuthErrorBanner } from '@/components/AuthErrorBanner/AuthErrorBanner';
import Captcha from '@/components/Captcha';
import { useCaptcha } from '@/components/Captcha/useCaptcha';
import { Flex } from '@/components/Layout/Flex/Flex';
import { useTrackErrors } from '@/hooks/useTrackErrors';
import { trackClick } from '@/lib/tagging';
import { useLoginIdManager } from '@/screens/login-id/hooks/useLoginIdManager';
import { emailRegex } from '@/utils/validations/email';

type FormValues = {
  email: string;
  captcha: string;
};

interface EmailFormProps {
  onBack: () => void;
}

export function EmailForm({ onBack }: EmailFormProps) {
  const { handleLoginId, captcha, isCaptchaAvailable } = useLoginIdManager();

  const { errors: sdkErrors } = useErrors();
  const { generalErrorMessages, fieldErrors } = useTrackErrors(
    sdkErrors,
    'login/error',
  );

  const untrustedData = useUntrustedData();
  const returnedUsername = String(
    untrustedData?.submittedFormData?.username || '',
  );

  const { captchaConfig, captchaProps, captchaValue } = useCaptcha(
    captcha || undefined,
  );

  const { t } = useTranslation(['loginId', 'common']);

  const {
    control,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      email: returnedUsername,
      captcha: '',
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      await handleLoginId({
        username: data.email,
        captcha: isCaptchaAvailable && captchaValue ? captchaValue : undefined,
      });
    } catch {
      setError('root', {
        type: 'manual',
        message: t('emailForm.errorGeneric'),
      });
    }
  };

  const handleBack = () => {
    trackClick('login/username/back');
    onBack();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Flex direction="column" gapY={16}>
        <AuthErrorBanner
          errors={
            errors.root?.message ? [errors.root.message] : generalErrorMessages
          }
        />
        <Controller
          name="email"
          control={control}
          rules={{
            required: t('emailForm.errorRequired'),
            pattern: {
              value: emailRegex,
              message: t('emailForm.errorInvalidFormat'),
            },
          }}
          render={({ field }) => (
            <Input
              {...field}
              name="email"
              type="email"
              placeholder={t('emailForm.emailPlaceholder')}
              status={
                errors.email?.message || fieldErrors.username
                  ? 'error'
                  : undefined
              }
              statusMessage={errors.email?.message ?? fieldErrors.username}>
              {t('emailForm.emailLabel')}
            </Input>
          )}
        />
        {isCaptchaAvailable && captchaConfig && (
          <Captcha
            captcha={captchaConfig}
            name="captcha"
            control={control}
            setError={setError}
            clearErrors={clearErrors}
            error={fieldErrors.captcha}
            rules={{ required: t('common:captcha.errorRequired') }}
            {...captchaProps}
          />
        )}
        <CtaContainer stack>
          <Cta
            className="c-submitCta"
            type="primary"
            submit
            disabled={isSubmitting}
            isWaiting={isSubmitting}>
            {t('emailForm.continueButtonText')}
          </Cta>
          <Cta type="ghost-inline" onClick={handleBack}>
            {t('emailForm.backButtonText')}
          </Cta>
        </CtaContainer>
      </Flex>
    </form>
  );
}
